import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArticleText,
  SectionImage,
  ImageTextSection,
  TextContent,
  List,
  ListItem as StyledListItem,
  ArticleSection as StyledArticleSection,
  SectionTitle,
} from './styles/ArticleSectionStyle';
import styled from 'styled-components';

const FullWidthImageContainer = styled.div`
  position: relative;
  width: 100%;
  margin: 2rem 0;
`;

const FullWidthImage = styled(SectionImage)`
  width: 100%;
  height: auto;
  display: block;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
`;

const ImageDescription = styled.span`
  position: absolute;
  bottom: 1rem;
  right: 1rem;
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.text.secondary};
  background-color: rgba(255, 255, 255, 0.9);
  padding: 0.5rem 1rem;
  border-radius: ${({ theme }) => theme.borderRadius.small};
`;

// New styled components for sub-headers section
const SubHeadersContainer = styled.div`
  margin-bottom: 1.5rem;
`;

const SubHeader = styled.div`
  margin: 1.5rem 0 0.75rem;

  h4 {
    font-size: 1.2rem;
    font-weight: 700;
    margin-bottom: 0.5rem;
    color: ${({ theme }) => theme.colors.text.primary};
  }

  p {
    font-size: 1.1rem;
    line-height: 1.8;
    margin: 0.5rem 0;
    text-align: right;
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

// Link styled component for sub-headers
const SubHeaderLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  background-color: ${({ theme }) => theme.colors.primary.light};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  color: ${({ theme }) => theme.colors.primary.main};
  font-weight: 600;
  text-decoration: none;
  transition: all 0.2s ease-in-out;
  width: fit-content;
  margin-right: auto;
  font-size: 1.2rem;

  &:hover {
    background-color: ${({ theme }) => theme.colors.primary.main};
  }

  .link-emoji {
    margin-left: 10px;
    font-size: 1.2rem;
  }
`;

// Section link at the end of sub-headers
const SectionLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  margin-top: 1.5rem;
  background-color: ${({ theme }) => theme.colors.primary.light};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  color: ${({ theme }) => theme.colors.primary.main};
  font-weight: 600;
  text-decoration: none;
  transition: all 0.2s ease-in-out;
  width: fit-content;
  margin-right: auto;
  font-size: 1.2rem;

  &:hover {
    background-color: ${({ theme }) => theme.colors.primary.main};
  }

  .link-emoji {
    margin-left: 10px;
    font-size: 1.2rem;
  }
`;

// Bold text styled component
const BoldArticleText = styled(ArticleText)`
  font-weight: 600;
`;

interface ListItem {
  subtitle?: string;
  text: string;
}

interface SubHeaderItem extends ListItem {
  link?: {
    url: string;
    text: string;
    target?: string;
    rel?: string;
  };
}

interface ArticleSectionProps {
  title: string;
  content: string | (string | ListItem | SubHeaderItem)[] | string[];
  type: 'text' | 'list' | 'text-image' | 'image' | 'sub-headers';
  imageUrl?: string;
  description?: string;
  isBold?: boolean;
  sectionLink?: {
    url: string;
    text: string;
  };
}

const ArticleSection: React.FC<ArticleSectionProps> = ({
  title,
  content,
  type,
  imageUrl,
  description,
  isBold = false,
  sectionLink,
}) => {
  const renderListItem = (item: string | ListItem, index: number) => {
    if (typeof item === 'string') {
      return (
        <StyledListItem
          key={index}
          dangerouslySetInnerHTML={{ __html: item }}
          style={isBold ? { fontWeight: 700 } : undefined}
        />
      );
    }
    return (
      <StyledListItem key={index}>
        {item.subtitle && <h4>{item.subtitle}</h4>}
        <p
          dangerouslySetInnerHTML={{ __html: item.text }}
          style={isBold ? { fontWeight: 700 } : undefined}
        />
      </StyledListItem>
    );
  };

  const renderSubHeader = (
    item: string | ListItem | SubHeaderItem,
    index: number
  ) => {
    if (typeof item === 'string') {
      return (
        <SubHeader key={index}>
          <p
            dangerouslySetInnerHTML={{ __html: item }}
            style={isBold ? { fontWeight: 700 } : undefined}
          />
        </SubHeader>
      );
    }

    const subHeaderItem = item as SubHeaderItem;

    return (
      <SubHeader key={index}>
        {subHeaderItem.subtitle && <h4>{subHeaderItem.subtitle}</h4>}
        <p
          dangerouslySetInnerHTML={{ __html: subHeaderItem.text }}
          style={isBold ? { fontWeight: 700 } : undefined}
        />
        {subHeaderItem.link && (
          <>
            {subHeaderItem.link.target ? (
              <a
                href={subHeaderItem.link.url}
                target={subHeaderItem.link.target}
                rel={subHeaderItem.link.rel}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  borderRadius: '4px',
                  color: '#0066CC',
                  fontWeight: 600,
                  textDecoration: 'none',
                  transition: 'all 0.2s ease-in-out',
                  width: 'fit-content',
                  fontSize: '1.2rem',
                }}
              >
                <span style={{ marginLeft: '10px', fontSize: '1.2rem' }}>
                  🔗
                </span>
                {subHeaderItem.link.text}
              </a>
            ) : (
              <SubHeaderLink to={subHeaderItem.link.url}>
                <span className="link-emoji">🔗</span>
                {subHeaderItem.link.text}
              </SubHeaderLink>
            )}
          </>
        )}
      </SubHeader>
    );
  };

  const TextComponent = isBold ? BoldArticleText : ArticleText;

  return (
    <StyledArticleSection>
      {type !== 'image' && <SectionTitle>{title}</SectionTitle>}

      {type === 'text' && (
        <>
          <TextComponent
            dangerouslySetInnerHTML={{
              __html: content as string,
            }}
          />
          {sectionLink && (
            <SectionLink to={sectionLink.url}>
              <span className="link-emoji">🔗</span>
              {sectionLink.text}
            </SectionLink>
          )}
        </>
      )}
      {type === 'list' && (
        <List>
          {(content as (string | ListItem)[]).map((item, index) =>
            renderListItem(item, index)
          )}
        </List>
      )}
      {type === 'text-image' && (
        <ImageTextSection>
          <TextContent>
            <TextComponent
              dangerouslySetInnerHTML={{
                __html: content as string,
              }}
            />
          </TextContent>
          <SectionImage src={imageUrl} alt={title} />
        </ImageTextSection>
      )}
      {type === 'image' && (
        <FullWidthImageContainer>
          <FullWidthImage src={imageUrl} alt={title} />
          <ImageDescription>{title}</ImageDescription>
        </FullWidthImageContainer>
      )}
      {type === 'sub-headers' && (
        <SubHeadersContainer>
          {description && (
            <TextComponent
              dangerouslySetInnerHTML={{
                __html: description,
              }}
            />
          )}
          {(content as (string | ListItem | SubHeaderItem)[]).map(
            (item, index) => renderSubHeader(item, index)
          )}
          {sectionLink && (
            <SectionLink to={sectionLink.url}>
              <span className="link-emoji">🔗</span>
              {sectionLink.text}
            </SectionLink>
          )}
        </SubHeadersContainer>
      )}
    </StyledArticleSection>
  );
};

export default ArticleSection;
